import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, lastValueFrom, of } from 'rxjs';
import {
  catchError, map, mergeMap, tap, timeout,
} from 'rxjs/operators';

import { User } from '@common/entities';

import { C } from 'src/app/@shared/constants';
import { ProtoUrl } from 'src/app/@shared/protoUrl';

import { ClientFilterObject } from '@common/filter.decorator';
import { ContentOptions, ContentService } from 'src/app/@core/content.service';
import { StorageService } from 'src/app/@core/storage.service';

import { RoleModel, RoleSource } from 'src/app/role/@shared/role.model';
import { UserModel, UserSource } from './user.model';
import { UserUpdateDto } from '@common/dtos';

@Injectable({
  providedIn: 'root',
})
export class UserService extends ContentService<UserModel, User> {
  private $currentUser: BehaviorSubject<UserModel | null> = new BehaviorSubject<UserModel | null>(null);
  private currentUser: UserModel | null;
  private path = 'users';

  private userRoles$: BehaviorSubject<RoleModel[]>;
  private userRolesStore: RoleModel[];

  constructor(private http: HttpClient,
    private storage: StorageService) {
    super();

    const user = this.storage.get('user') as User;

    if (user) {
      this.updateCurrentUser(user, true).catch();
    }
  }

  public getCurrentUser(): Observable<UserModel | null> {
    return this.$currentUser.asObservable();
  }

  public loadContent(id: string, options: ContentOptions<User> = {}): Observable<UserModel[] | null> {
    if (!options.type) {
      console.warn('[UserService] loadContent(id, options) no type provided -> defaults to loadListContent');
    }

    if (options.type !== 'detail') {
      return this.loadListContent(id, options);
    }

    return this.loadDetailContent(id, options).pipe(map((item: UserModel | null) => {
      return item
        ? [item]
        : null;
    }));
  }

  public createUser(attributes: UserUpdateDto, storeId: string): Promise<UserModel> {
    return lastValueFrom(this.http.post<User>(ProtoUrl.getUrl(this.path), attributes)
      .pipe(map((item: User) => {
        return new UserModel(item);
      }),
      tap((item: UserModel) => {
        if (this.metadataStore[storeId]) {
          this.metadataStore[storeId].totalCount += 1;
        }

        return this.updateItem(item, storeId, true);
      })));
  }

  public patch(id: string, attributes: Partial<UserSource>): Promise<UserModel> {
    return lastValueFrom(this.http.patch<User>(ProtoUrl.getUrl(`${this.path}/${id}`), attributes)
      .pipe(map((item: User) => {
        if (item.id === this.currentUser?.id) {
          this.updateCurrentUser(item).catch();
        }

        return new UserModel(item);
      }),
      tap((item: UserModel) => { return this.updateItem(item); })));
  }

  public getCurrentUserFromStore(): UserModel | null {
    const user: unknown = this.storage.get('user');

    if (user) {
      this.currentUser = new UserModel(user as User);

      return this.currentUser;
    }

    return null;
  }

  public async updateCurrentUser(user: User, refresh?: boolean): Promise<void> {
    let updatedUser: User;

    if (refresh) {
      try {
        updatedUser = await lastValueFrom(this.http.get<User>(ProtoUrl.getUrl(`${this.path}/${user.id}`, {})));
      } catch (error) {
        console.error(error);
        updatedUser = user;
      }
    } else {
      updatedUser = user;
    }

    this.storage.set('user', JSON.parse(JSON.stringify(updatedUser)));
    this.currentUser = new UserModel(updatedUser);
    this.$currentUser.next(this.currentUser);
  }

  public removeCurrentUser(): void {
    this.storage.remove('user');

    if (!this.currentUser) { return; }

    this.removeItem(this.currentUser.id);
    this.currentUser = null;
  }

  public deleteAccount(email: string, password: string): Promise<any> {
    if (!this.currentUser) { return Promise.resolve(); }

    const url = ProtoUrl.getUrl(`${this.path}/${this.currentUser.id}/delete-account`);

    return lastValueFrom(this.http.post(url, {
      email,
      password,
    }));
  }

  public deleteUserById(id: string): Promise<any> {
    return lastValueFrom(this.http.delete(ProtoUrl.getUrl(`${this.path}/${id}`))
      .pipe(tap(() => {
        return this.removeItem(id);
      })));
  }

  public blockUserById(blockedUserId: string): Promise<any> {
    if (!this.currentUser) { return Promise.resolve(); }

    const url = ProtoUrl.getUrl(`${this.path}/${this.currentUser.id}/blockedUsers`);

    return lastValueFrom(this.http.post(url, {
      blockedUserId,
    }));
  }

  public getRoles(): Observable<RoleModel[]> {
    if (this.userRoles$) {
      return this.userRoles$.asObservable();
    }

    this.userRoles$ = new BehaviorSubject<RoleModel[]>([]);

    return this.loadUserRoles()
      .pipe(catchError((error) => {
        console.error('[UserService] loadUserRoles error', error);

        return of([]);
      }),
      mergeMap(() => { return this.userRoles$.asObservable(); }));
  }

  public loadUserRoles(): Observable<RoleModel[]> {
    const url = ProtoUrl.getUrl('roles');

    return this.http.get<RoleSource[]>(url, {
      observe: 'response',
    })
      .pipe(timeout(C.DEFAULT_TIMEOUT),
        map((response: HttpResponse<RoleSource[]>) => { return response.body || []; }),
        map((items: RoleSource[]) => { return items.map((item) => { return new RoleModel(item); }); }),
        tap((items: RoleModel[]) => {
          this.updateUserRoles(items);
        }));
  }

  protected loadListContent(id: string, options: ContentOptions<User>): Observable<UserModel[]> {
    const filter: ClientFilterObject<User> = this.getNormalizedFilter(id, options.filter);
    const url = ProtoUrl.getUrl(this.path, {
      filter,
    });

    console.info('[UserService] load items', url);
    let totalCount: number;

    return this.http.get<User[]>(url, {
      observe: 'response',
    })
      .pipe(
        timeout(C.DEFAULT_TIMEOUT),
        tap((response: HttpResponse<User[]>) => {
          const { headers } = response;

          totalCount = parseInt(headers.get('x-total-count')) || (response.body?.length || 0);
        }),
        map((response: HttpResponse<User[]>) => { return response.body || []; }),
        map((items: User[]) => {
          return items.map((item) => { return new UserModel(item); });
        }),
        tap((items: UserModel[]) => {
          this.setItems(items, id, filter, totalCount);
        }),
      );
  }

  protected loadDetailContent(id: string, options: ContentOptions<User>): Observable<UserModel> {
    const { filter } = options;

    const url = ProtoUrl.getUrl(`${this.path}/${id}`, {
      filter,
    });

    return this.http.get<User>(url)
      .pipe(timeout(C.DEFAULT_TIMEOUT),
        map((item: User) => {
          if (item) {
            if (item.id === this.currentUser?.id) {
              this.updateCurrentUser(item).catch();
            }

            return new UserModel(item);
          }

          return null;
        }),
        tap((item: UserModel) => {
          this.updateItem(item);
        }));
  }

  private updateUserRoles(roles: RoleModel[]): void {
    if (!roles) {
      console.error('[AssessmentReportService] Error updating roles. Roles are required!');

      return;
    }

    this.userRolesStore = roles;

    if (this.userRoles$) {
      // slice is needed to create a new array and trigger change detection
      this.userRoles$.next(this.userRolesStore.slice());
    } else {
      this.userRoles$ = new BehaviorSubject<RoleModel[]>(roles);
    }
  }
}
